import { DatePipe, DOCUMENT } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Inject, NgModule, NgZone } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { UpgradeModule } from '@angular/upgrade/static';
import { Apollo, ApolloBoostModule, InMemoryCache } from 'apollo-angular-boost';
import { HttpLinkModule } from 'apollo-angular-link-http';
import { onError } from 'apollo-link-error';
import { HttpLink } from 'apollo-link-http';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { CookieService } from 'ngx-cookie-service';
import { NgxFileDropModule } from 'ngx-file-drop';
import { MomentModule } from 'ngx-moment';
import { distinctUntilChanged } from 'rxjs/operators';
import { AppComponent } from './app.component';
import { AppRouting } from './app.routing';
import { LoginModule } from './login/login.module';
import { SharedModule } from './shared/shared.module';
import { SignupModule } from './signup/signup.module';
import { QuillModule } from 'ngx-quill';
import { OrganizationService } from '../services/organization.service';
import { AccountService } from '../services/account.service';
import { DynamicScriptLoaderService } from '../services/dynamic-script-loader.service';
import { PusherService } from '../services/pusher.service';
import { UtilityService } from '../services/utility.service';
import { NotificationService } from '../services/notification.service';
import { AccountModule } from './account/account.module';
import { OrganizationModule } from './organization/organization.module';
import { WatchAccountService } from '../services/watch-account.service';
import { WatchOrganizationService } from '../services/watch-organization.service';
import { ProfileService } from '../services/profile.service';
import { NotificationCountersService } from '../services/notification-counters.service';
import { SubscriptionNotificationService } from '../services/subscription-notification.service';
import { SubscriptionService } from '../services/subscription.service';
import { CandidateService } from '../services/candidate.service';
import { WatchFunnelService } from '../services/watch-funnel.service';
import { FunnelService } from '../services/funnel.service';
import { GuestModule } from './guest/guest.module';
import { ReviewService } from '../services/review.service';

declare let gtag: any;

/*
class Ng1Ng2UrlHandlingStrategy implements UrlHandlingStrategy {
  shouldProcessUrl(url) {
    return true;
    return url.toString().startsWith('/organization/');
  }
  extract(url) {
    return url;
  }
  merge(url, whole) {
    return url;
  }
}

declare var angular: any;
*/

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule, // has to be before angular materials
    RouterModule,
    SharedModule,
    FormsModule,
    UpgradeModule,
    HttpClientModule,
    ApolloBoostModule,
    HttpLinkModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    LoginModule,
    SignupModule,
    MomentModule,
    NgxFileDropModule,
    AccountModule,
    OrganizationModule,
    GuestModule,
    AppRouting,
    ModalModule.forRoot(),
    BsDropdownModule.forRoot(),
    PaginationModule.forRoot(),
    QuillModule.forRoot(),
  ],
  entryComponents: [],
  providers: [
    // Providing a custom url handling strategy to tell the Angular 2 router
    // which routes it is responsible for.
    // { provide: UrlHandlingStrategy, useClass: Ng1Ng2UrlHandlingStrategy },
    DatePipe,
    CookieService,
    OrganizationService,
    AccountService,
    DynamicScriptLoaderService,
    UtilityService,
    PusherService,
    NotificationService,
    WatchAccountService,
    WatchOrganizationService,
    ProfileService,
    NotificationCountersService,
    SubscriptionNotificationService,
    SubscriptionService,
    CandidateService,
    ReviewService,
    WatchFunnelService,
    FunnelService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    @Inject(DOCUMENT) private document: any,
    apollo: Apollo,
    notificationService: NotificationService,
    protected zone: NgZone,
    public router: Router,
  ) {
    let csrfToken = document.querySelector(`meta[name='csrf-token']`).content;

    console.log('>>> Bootstrapping AppModule - Start');

    this.zone.onError.subscribe(err => {
      //console.warn('BOOOOOM');
      // console.warn(err);
      throw err;
    });

    /* trigger an error in 1 sec
    setTimeout(() => {
      throw new Error();
    }, 1000);

    setTimeout(() => {
      Promise.reject('unhandled');
    }, 1000);
    */

    let http = new HttpLink({
      uri: '/graphql',
      // credentials: 'include', -> include if if requests are made from different site
      fetchOptions: { mode: 'cors', credentials: 'same-origin' }, // was 'include'
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    });

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path }) =>
          console.warn(
            `*** [GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
          ),
        );

      if (networkError) {
        notificationService.networkStatus().next(false);
        console.warn(`*** [Network error]: ${networkError}`);
      }
    });

    apollo.create({
      link: errorLink.concat(http),
      defaultOptions: {
        query: {
          fetchPolicy: 'network-only',
        },
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: true,
        },
      },
      cache: new InMemoryCache({
        dataIdFromObject(obj) {
          return obj.__typename && obj.id && `${obj.__typename}_${obj.id}`;
        },
      }),
    });

    /*
    apolloBoost.create({
      uri: '/graphql',
      onError: ({ graphQLErrors, networkError }) => {
        console.log('!!! ARG AN ERROR!!! ');
      },
    });
*/

    // Google Analytics
    router.events
      .pipe(
        distinctUntilChanged((previous: any, current: any) => {
          // Subscribe to any `NavigationEnd` events where the url has changed
          if (current instanceof NavigationEnd) {
            return previous.url === current.url;
          }
          return true;
        }),
      )
      .subscribe((x: any) => {
        //console.log('router.change', x);
        if (typeof gtag !== 'undefined') {
          gtag('send', 'pageview', x.url);
          gtag('config', 'G-5XBQWP0V0T', { page_path: x.url });
        }
      });

    /*
    // Helpscout interfere with Angular removing the app dom element, so we remove it ourselves manually
    let element = this.document.getElementbyId('app-loading-pending');
    if (element) {
      element.style.display = 'none';
    }
    */

    console.log('>>> Bootstrapping AppModule - end');
  }
}

console.log('app.module.ts');
